import { Input } from '@/components/ui/input';
import type { ControllerRenderProps } from 'react-hook-form';
import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import type { PolicySettingsFormData } from '../types';
import {
  shouldRenderAsSelect,
  getEnumOptions,
} from '../helpers/fieldDerivation';

interface StringSettingFieldProps {
  setting: PolicySetting;
  field: ControllerRenderProps<PolicySettingsFormData, string>;
  disabled?: boolean;
}

/**
 * String setting field component
 * Renders either a text input or a select dropdown
 * Derives component type from validation.pattern (enum detection)
 *
 * @param setting - The policy setting configuration
 * @param field - The react-hook-form field controller props
 * @param disabled - Whether the field is disabled
 */
export function StringSettingField({
  setting,
  field,
  disabled = false,
}: StringSettingFieldProps) {
  // Derive from schema: Check if pattern matches enum format
  // Falls back to known constants for edge cases
  const isSelect = shouldRenderAsSelect(setting);

  if (isSelect) {
    const options = getEnumOptions(setting);

    // Get value with fallback chain: field.value -> setting.value -> setting.defaultValue -> first option -> ''
    const getSelectValue = (): string => {
      if (typeof field.value === 'string') return field.value;
      if (typeof setting.value === 'string') return setting.value;
      if (typeof setting.defaultValue === 'string') return setting.defaultValue;
      if (options.length > 0) return options[0].value;
      return '';
    };

    return (
      <select
        id={setting.key}
        value={getSelectValue()}
        onChange={e => field.onChange(e.target.value)}
        onBlur={field.onBlur}
        aria-label={setting.name}
        disabled={disabled}
        className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  // Default: Text input with pattern validation placeholder
  // Value fallback chain: field.value -> setting.value -> setting.defaultValue -> ''
  // This ensures we always have a valid string value
  const getTextValue = (): string => {
    if (typeof field.value === 'string') return field.value;
    if (setting.value !== undefined && setting.value !== null) {
      return String(setting.value);
    }
    if (typeof setting.defaultValue === 'string') return setting.defaultValue;
    return '';
  };

  // Use field.value directly, with fallback only for initial render
  const inputValue =
    typeof field.value === 'string' ? field.value : getTextValue();

  // Hardcoded maxLength for excessiveCommandsThreshold (10 digits)
  const getMaxLength = (): number | undefined => {
    if (setting.key === 'excessiveCommandsThreshold') {
      return 10;
    }
    return setting.validation?.maxLength;
  };

  return (
    <Input
      type="text"
      id={setting.key}
      {...field}
      value={inputValue}
      disabled={disabled}
      maxLength={getMaxLength()}
      placeholder={
        setting.validation?.pattern
          ? `Pattern: ${setting.validation.pattern}`
          : ''
      }
    />
  );
}
